import { useMemo, useState, type ReactNode } from 'react';
import { ChevronDown, ExternalLink, Plus, X } from 'lucide-react';
import { Modal } from '../../Modal';
import { Badge, Button, Card, IconButton, Input, OverflowMenu, Switch, Typography, type OverflowMenuAction } from '../ui';
import { editPersonCard } from '../../../lib/contact-book';
import { RELATIONSHIP_PROFILE_CAPS, type CardEdit, type StoredImportantDate, type StoredSocialProfile } from '../../../lib/person-card-edit';
import { HEBREW_MONTHS, hebrewMonthName, type HebrewMonth } from '../../../lib/hebrew-calendar';
import {
  CLOSENESS_WIRE,
  DATE_KIND_WIRE,
  FEELING_WIRE,
  NETWORK_NAMES,
  REGISTER_WIRE,
  formatRelateDate,
  languageName,
  learnedStyleParts,
  type CardCloseness,
  type CardFeeling,
  type CardRegister,
  type CardRelate,
  cardSummaryKeys,
  civilToday,
  currentHebrewYear,
  dateDraftValue,
  dateKindLabelKey,
  hebrewMonthsForYear,
  switchDateDraftCalendar,
  type DateDraft,
  daysUntilKey,
  formatCardDate,
  gregorianMonthNames,
  type CardDateKind,
  type CardDateRow,
  type CardNetwork,
  type CardOrganization,
  type CardRelationshipRow,
  type CardProfileRow,
  type PersonCardModel,
} from '../../../lib/person-card';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useNarrow } from '../../../lib/use-narrow';
import { useProductName } from '../../../lib/use-brand';
import { useTranslation } from '../../../lib/i18n';
import styles from './contacts.module.css';

/**
 * The person card's structured sections (plan P1.16) — Relationship, Important dates, Social profiles and
 * Work — under the identity on the contact detail screen. Everything shown comes from `personCardModel`
 * (`lib/person-card.ts`), which iOS and Android build identically from the same entry; this file only draws
 * it and collects edits. Every edit goes through `editPersonCard` → `/api/contact-person` as the user's own
 * value; "Forget this" asks first, with the copy that says the agent will not use or look it up again.
 *
 * A value the agent INFERRED wears "Suggested by Symphony"; editing it makes it the user's. The derived
 * signals (family / work / minor / closeness) are never drawn — they are behaviour, not content (§1.1).
 */

const PAGE = 'contact_detail';
const DATE_KINDS: CardDateKind[] = ['birthday', 'anniversary', 'memorial', 'nameDay', 'custom'];
const KIND_WIRE = DATE_KIND_WIRE;
const NETWORK_ORDER: CardNetwork[] = ['instagram', 'linkedin', 'x', 'facebook', 'tiktok', 'youtube', 'github', 'website', 'other'];

type Dialog =
  | { kind: 'relationship'; editing: CardRelationshipRow | null }
  | { kind: 'date'; editing: CardDateRow | null }
  | { kind: 'social' }
  | { kind: 'work'; editing: CardOrganization | null }
  | { kind: 'relate'; row: RelateRow }
  | { kind: 'forget'; edit: CardEdit };

/** The "How you relate" rows a dialog edits (the switch saves in place). */
type RelateRow = 'feeling' | 'closeness' | 'tone' | 'address' | 'avoid';

const FEELINGS: CardFeeling[] = ['close', 'warm', 'neutral', 'distant'];
const CLOSENESSES: CardCloseness[] = ['innerCircle', 'close', 'regular', 'peripheral'];
const REGISTERS: CardRegister[] = ['intimate', 'casual', 'friendlyProfessional', 'formal'];

/** The wire value a relationship row was read as — the contract's `target` (relation + anchor). */
function relationshipWire(row: CardRelationshipRow): { relation: string; relatedToPersonId?: string } {
  return { relation: row.text, ...(row.relatedToPersonId ? { relatedToPersonId: row.relatedToPersonId } : {}) };
}

/** The wire value a date row was read as — the row's key on the server (`lib/person-card-edit.ts`). */
function dateWire(row: CardDateRow): StoredImportantDate {
  return {
    kind: KIND_WIRE[row.kind],
    calendar: row.calendar === 'hebrew' ? 'HEBREW' : 'GREGORIAN',
    day: row.day,
    ...(row.month ? { month: row.month } : {}),
    ...(row.hebrewMonth ? { hebrewMonth: row.hebrewMonth } : {}),
    ...(row.year ? { year: row.year } : {}),
    ...(row.label ? { label: row.label } : {}),
  };
}

function profileWire(row: CardProfileRow): StoredSocialProfile {
  return { network: row.network.toUpperCase(), ...(row.handle ? { handle: row.handle } : {}), ...(row.opens ? { url: row.opens } : {}) };
}

export function PersonCardSections({ personId, model }: { personId: string; model: PersonCardModel }) {
  const { t, i18n } = useTranslation();
  const productName = useProductName();
  const { logPageClickBi } = useGenericBi();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const locale = i18n.language || 'en';
  const close = () => setDialog(null);
  const click = (button_name: string) => logPageClickBi({ page_name: PAGE, button_name });

  const suggested = <Badge label={t('people.card.source.inferred', { productName })} />;
  const forget = (edit: CardEdit, button: string) => () => {
    click(button);
    setDialog({ kind: 'forget', edit });
  };

  const addButton = (onClick: () => void, label: string) => (
    <Button variant="text" size="small" startIcon={<Plus size={16} aria-hidden />} onClick={onClick}>
      {label}
    </Button>
  );

  return (
    <>
      {/* How you two keep in touch (P3.1) — observed, read-only, and only what Symphony sees. */}
      {model.summary ? (
        <div className={styles.detailSection} data-testid="person-card-summary">
          <Typography variant="h3" color="primary">
            {t('people.card.summary')}
          </Typography>
          <Card variant="sunken" size="sm" className={styles.channelCard}>
            <span className={styles.channelText}>
              <Typography variant="p2" color="primary">
                {cardSummaryKeys(model.summary)
                  .map((key) => t(key))
                  .join(' · ')}
              </Typography>
              <Typography variant="p3" color="secondary">
                {t('people.card.summary.note', { productName })}
              </Typography>
            </span>
          </Card>
        </div>
      ) : null}

      {/* Relationship */}
      <div className={styles.detailSection}>
        <div className={styles.sectionHead}>
          <Typography variant="h3" color="primary">
            {t('people.card.relationship')}
          </Typography>
          {model.canAdd.relationships
            ? addButton(
                () => {
                  click('add_relationship');
                  setDialog({ kind: 'relationship', editing: null });
                },
                model.relationships.length ? t('people.card.relationship.add') : t('common.add'),
              )
            : null}
        </div>
        {model.relationships.length ? (
          <div className={styles.channelCards}>
            {model.relationships.map((row) => (
              <Card key={row.text} variant="sunken" size="sm" className={styles.channelCard}>
                <div className={styles.channelRow}>
                  <span className={styles.channelText}>
                    <Typography variant="h4" color="primary" className={styles.channelValue}>
                      {row.text}
                    </Typography>
                    {row.inferred ? <span className={styles.personCardBadge}>{suggested}</span> : null}
                  </span>
                  <RowMenu
                    ariaLabel={row.text}
                    onEdit={() => {
                      click('edit_relationship');
                      setDialog({ kind: 'relationship', editing: row });
                    }}
                    onForget={forget({ op: 'forget', field: 'relationship', target: relationshipWire(row) }, 'forget_relationship')}
                  />
                </div>
              </Card>
            ))}
          </div>
        ) : null}
      </div>

      {/* How you relate (P2.3) — right after the relationships, identical on web, iOS and Android. */}
      <RelateSection
        personId={personId}
        relate={model.relate}
        onOpen={(row, button) => {
          click(button);
          setDialog({ kind: 'relate', row });
        }}
        onForget={(edit, button) => forget(edit, button)()}
      />

      {/* Important dates */}
      <div className={styles.detailSection}>
        <div className={styles.sectionHead}>
          <Typography variant="h3" color="primary">
            {t('people.card.dates')}
          </Typography>
          {model.canAdd.importantDates
            ? addButton(() => {
                click('add_important_date');
                setDialog({ kind: 'date', editing: null });
              }, t('common.add'))
            : null}
        </div>
        {model.importantDates.length ? (
          <div className={styles.channelCards}>
            {model.importantDates.map((row) => {
              const title = row.kind === 'custom' && row.label ? row.label : t(dateKindLabelKey(row.kind));
              const when = [
                row.age !== null ? t('people.card.dates.turns', { age: row.age }) : '',
                row.daysUntil !== null ? t(daysUntilKey(row.daysUntil, locale) as string, { count: row.daysUntil }) : '',
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <Card
                  key={`${row.kind}|${row.calendar}|${row.month ?? row.hebrewMonth}|${row.day}|${row.label ?? ''}`}
                  variant="sunken"
                  size="sm"
                  className={styles.channelCard}
                >
                  <div className={styles.channelRow}>
                    <span className={styles.channelText}>
                      <Typography variant="h4" color="primary">
                        {title}
                      </Typography>
                      <Typography variant="p2" color="secondary">
                        {formatCardDate(row, locale)}
                        {row.calendar === 'hebrew' ? ` · ${t('people.card.dates.hebrew')}` : ''}
                      </Typography>
                      {when ? (
                        <Typography variant="p3" color="secondary">
                          {when}
                        </Typography>
                      ) : null}
                      {row.inferred ? <span className={styles.personCardBadge}>{suggested}</span> : null}
                    </span>
                    <RowMenu
                      ariaLabel={title}
                      onEdit={() => {
                        click('edit_important_date');
                        setDialog({ kind: 'date', editing: row });
                      }}
                      onForget={forget({ op: 'forget', field: 'importantDate', target: dateWire(row) }, 'forget_important_date')}
                    />
                  </div>
                </Card>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Social profiles */}
      <div className={styles.detailSection}>
        <div className={styles.sectionHead}>
          <Typography variant="h3" color="primary">
            {t('people.card.social')}
          </Typography>
          {model.canAdd.socialProfiles
            ? addButton(() => {
                click('add_social_profile');
                setDialog({ kind: 'social' });
              }, t('common.add'))
            : null}
        </div>
        {model.socialProfiles.length ? (
          <div className={styles.channelCards}>
            {model.socialProfiles.map((row) => {
              const name = networkName(row.network, t);
              return (
                <Card key={`${row.network}|${row.handle ?? row.opens}`} variant="sunken" size="sm" className={styles.channelCard}>
                  <div className={styles.channelRow}>
                    <span className={styles.channelText}>
                      <Typography variant="h4" color="primary">
                        {name}
                      </Typography>
                      <Typography variant="p2" color="secondary" className={styles.channelValue}>
                        {/* A handle reads left-to-right in every language; `bdi` keeps it whole inside RTL text. */}
                        <bdi>{row.handle ? `@${row.handle}` : row.opens}</bdi>
                      </Typography>
                      {row.inferred ? <span className={styles.personCardBadge}>{suggested}</span> : null}
                    </span>
                    <span className={styles.personCardRowEnd}>
                      {row.opens ? (
                        <Button
                          variant="text"
                          size="small"
                          href={row.opens}
                          external
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${name} ${row.handle ?? ''}`.trim()}
                          startIcon={<ExternalLink size={16} aria-hidden />}
                          onClick={() => click('open_social_profile')}
                        >
                          {t('common.open')}
                        </Button>
                      ) : null}
                      <RowMenu
                        ariaLabel={name}
                        onForget={forget({ op: 'forget', field: 'socialProfile', target: profileWire(row) }, 'forget_social_profile')}
                      />
                    </span>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Work */}
      <div className={styles.detailSection}>
        <div className={styles.sectionHead}>
          <Typography variant="h3" color="primary">
            {t('people.card.work')}
          </Typography>
          {!model.organization
            ? addButton(() => {
                click('add_work');
                setDialog({ kind: 'work', editing: null });
              }, t('common.add'))
            : null}
        </div>
        {model.organization ? (
          <Card variant="sunken" size="sm" className={styles.channelCard}>
            <div className={styles.channelRow}>
              <span className={styles.channelText}>
                {model.organization.company ? (
                  <Typography variant="h4" color="primary">
                    {model.organization.company}
                  </Typography>
                ) : null}
                {model.organization.title ? (
                  <Typography variant="p2" color="secondary">
                    {model.organization.title}
                  </Typography>
                ) : null}
                {model.organization.website ? (
                  <Typography variant="p3" color="secondary" className={styles.channelValue}>
                    <bdi>{model.organization.website}</bdi>
                  </Typography>
                ) : null}
                {model.organization.inferred ? <span className={styles.personCardBadge}>{suggested}</span> : null}
              </span>
              <RowMenu
                ariaLabel={t('people.card.work')}
                onEdit={() => {
                  click('edit_work');
                  setDialog({ kind: 'work', editing: model.organization });
                }}
                onForget={forget({ op: 'forget', field: 'organization' }, 'forget_work')}
              />
            </div>
          </Card>
        ) : null}
      </div>

      {dialog?.kind === 'relationship' ? <RelationshipDialog personId={personId} editing={dialog.editing} onClose={close} /> : null}
      {dialog?.kind === 'date' ? <DateDialog personId={personId} editing={dialog.editing} onClose={close} /> : null}
      {dialog?.kind === 'social' ? <SocialDialog personId={personId} onClose={close} /> : null}
      {dialog?.kind === 'work' ? <WorkDialog personId={personId} editing={dialog.editing} onClose={close} /> : null}
      {dialog?.kind === 'relate' ? <RelateDialog personId={personId} row={dialog.row} relate={model.relate} onClose={close} /> : null}
      {dialog?.kind === 'forget' ? <ForgetDialog personId={personId} edit={dialog.edit} onClose={close} /> : null}
    </>
  );
}

function networkName(network: CardNetwork, t: (key: string) => string): string {
  if (network === 'website') return t('people.card.social.website');
  if (network === 'other') return t('people.card.social.other');
  return NETWORK_NAMES[network];
}

/** A row's Edit / Forget actions, in the row overflow menu every list in the app uses. */
function RowMenu({ ariaLabel, onEdit, onForget }: { ariaLabel: string; onEdit?: () => void; onForget?: () => void }) {
  const { t } = useTranslation();
  const actions: OverflowMenuAction[] = [
    ...(onEdit ? [{ key: 'edit', label: t('common.edit'), onSelect: onEdit }] : []),
    ...(onForget ? [{ key: 'forget', label: t('people.card.forget'), onSelect: onForget, destructive: true }] : []),
  ];
  return <OverflowMenu ariaLabel={ariaLabel} actions={actions} />;
}

/** Saves one edit, keeping the dialog open with the reader's input on a refusal. */
function useCardSave(personId: string, onClose: () => void) {
  const { logPageClickBi } = useGenericBi();
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const save = async (edit: CardEdit, button: string) => {
    if (busy) return;
    setBusy(true);
    setErrorKey(null);
    logPageClickBi({ page_name: PAGE, button_name: button });
    const result = await editPersonCard(personId, edit);
    setBusy(false);
    if (!result.ok) {
      setErrorKey(result.messageKey);
      return;
    }
    onClose();
  };
  return { busy, errorKey, save, setErrorKey };
}

function DialogShell({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer: ReactNode }) {
  const narrow = useNarrow();
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="minimum"
      maxHeightPx={650}
      scrollBody
      presentation={narrow ? 'sheet' : 'centered'}
      footer={footer}
    >
      <div className={styles.addChannelForm}>{children}</div>
    </Modal>
  );
}

function ErrorLine({ messageKey }: { messageKey: string | null }) {
  const { t } = useTranslation();
  if (!messageKey) return null;
  return (
    <Typography variant="p2" color="inherit" role="alert" className={styles.personCardError}>
      {t(messageKey)}
    </Typography>
  );
}

function RelationshipDialog({ personId, editing, onClose }: { personId: string; editing: CardRelationshipRow | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(editing?.text ?? '');
  const { busy, errorKey, save } = useCardSave(personId, onClose);
  const submit = () => {
    const relation = value.trim();
    if (!relation) return;
    void save(
      editing
        ? {
            op: 'set',
            field: 'relationship',
            target: relationshipWire(editing),
            // An anchor ("Yoni's teacher") the relation already had is kept; only the words change.
            value: { relation, ...(editing.relatedToPersonId ? { relatedToPersonId: editing.relatedToPersonId } : {}) },
          }
        : { op: 'set', field: 'relationship', value: { relation } },
      editing ? 'save_relationship_edit' : 'save_relationship',
    );
  };
  return (
    <DialogShell
      title={t('people.card.relationship')}
      onClose={onClose}
      footer={
        <Button fullWidth loading={busy} disabled={!value.trim()} onClick={submit}>
          {t('common.save')}
        </Button>
      }
    >
      <Input
        label={t('people.card.relationship')}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder={t('people.card.relationship.placeholder')}
        maxLength={200}
        status={errorKey ? 'error' : 'default'}
        statusMessage={errorKey ? t(errorKey) : undefined}
        autoFocus
      />
    </DialogShell>
  );
}

function DateDialog({ personId, editing, onClose }: { personId: string; editing: CardDateRow | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const [kind, setKind] = useState<CardDateKind>(editing?.kind ?? 'birthday');
  const [draft, setDraft] = useState<DateDraft>({
    hebrew: editing?.calendar === 'hebrew',
    day: editing ? String(editing.day) : '',
    month: editing?.month ?? 1,
    hebrewMonth: editing?.hebrewMonth ?? 'TISHREI',
    year: editing?.year ? String(editing.year) : '',
  });
  const { hebrew, month, hebrewMonth, day, year } = draft;
  const patch = (next: Partial<DateDraft>) => setDraft((d) => ({ ...d, ...next }));
  const [label, setLabel] = useState(editing?.label ?? '');
  const { busy, errorKey, save, setErrorKey } = useCardSave(personId, onClose);
  const gregorianNames = useMemo(() => gregorianMonthNames(locale), [locale]);
  const hebrewNames = useMemo(
    () => Object.fromEntries(HEBREW_MONTHS.map((m) => [m, hebrewMonthName(m, locale)])) as Record<HebrewMonth, string>,
    [locale],
  );
  // A Hebrew date's year is a Hebrew year: the field hints the current one ("5787"), never a civil "1983".
  const today = useMemo(() => civilToday(new Date()), []);
  const yearHint = hebrew ? String(currentHebrewYear(today)) : undefined;

  const kindActions: OverflowMenuAction[] = DATE_KINDS.map((k) => ({ key: k, label: t(dateKindLabelKey(k)), onSelect: () => setKind(k) }));
  // With a year typed, only the months that year has (Adar I/II in a leap year, Adar in a common one).
  const monthActions: OverflowMenuAction[] = hebrew
    ? hebrewMonthsForYear(year).map((m) => ({ key: m, label: hebrewNames[m], onSelect: () => patch({ hebrewMonth: m }) }))
    : gregorianNames.map((name, i) => ({ key: String(i + 1), label: name, onSelect: () => patch({ month: i + 1 }) }));

  const submit = () => {
    const result = dateDraftValue({ ...draft, kind, label }, today);
    if (!result.ok) {
      setErrorKey(result.errorKey);
      return;
    }
    const value: StoredImportantDate = result.value;
    void save(
      editing ? { op: 'set', field: 'importantDate', target: dateWire(editing), value } : { op: 'set', field: 'importantDate', value },
      editing ? 'save_important_date_edit' : 'save_important_date',
    );
  };

  return (
    <DialogShell
      title={t('people.card.dates')}
      onClose={onClose}
      footer={
        <Button fullWidth loading={busy} disabled={!day.trim()} onClick={submit}>
          {t('common.save')}
        </Button>
      }
    >
      <div className={styles.personCardField}>
        <Typography variant="h4" color="primary">
          {t('people.card.dates.kind')}
        </Typography>
        <OverflowMenu label={t(dateKindLabelKey(kind))} ariaLabel={t('people.card.dates.kind')} actions={kindActions} selectedKey={kind} />
      </div>
      {kind === 'custom' ? (
        <Input label={t('people.card.dates.label')} value={label} onChange={(e) => setLabel(e.target.value)} maxLength={100} />
      ) : null}
      <Switch checked={hebrew} onChange={(on: boolean) => setDraft((d) => switchDateDraftCalendar(d, on))} label={t('people.card.dates.hebrew')} />
      <div className={styles.personCardDateRow}>
        <Input
          label={t('people.card.dates.day')}
          value={day}
          onChange={(e) => patch({ day: e.target.value.replace(/\D/g, '').slice(0, 2) })}
          inputMode="numeric"
          className={styles.personCardDay}
        />
        <div className={styles.personCardField}>
          <Typography variant="h4" color="primary">
            {t('people.card.dates.month')}
          </Typography>
          <OverflowMenu
            label={hebrew ? hebrewNames[hebrewMonth] : gregorianNames[month - 1]}
            ariaLabel={t('people.card.dates.month')}
            actions={monthActions}
            selectedKey={hebrew ? hebrewMonth : String(month)}
          />
        </div>
      </div>
      <Input
        label={t('people.card.dates.year')}
        optionalLabel={t('common.optional')}
        value={year}
        onChange={(e) => patch({ year: e.target.value.replace(/\D/g, '').slice(0, 4) })}
        inputMode="numeric"
        placeholder={yearHint}
      />
      <ErrorLine messageKey={errorKey} />
    </DialogShell>
  );
}

function SocialDialog({ personId, onClose }: { personId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [network, setNetwork] = useState<CardNetwork>('instagram');
  const [value, setValue] = useState('');
  const { busy, errorKey, save } = useCardSave(personId, onClose);
  const actions: OverflowMenuAction[] = NETWORK_ORDER.map((n) => ({ key: n, label: networkName(n, t), onSelect: () => setNetwork(n) }));
  const submit = () => {
    const typed = value.trim();
    if (!typed) return;
    // A homepage is only ever a link; elsewhere a typed link is the url and anything else the handle.
    const isUrl = network === 'website' || /^https?:\/\//i.test(typed);
    void save(
      { op: 'set', field: 'socialProfile', value: { network: network.toUpperCase(), ...(isUrl ? { url: typed } : { handle: typed }) } },
      'save_social_profile',
    );
  };
  return (
    <DialogShell
      title={t('people.card.social')}
      onClose={onClose}
      footer={
        <Button fullWidth loading={busy} disabled={!value.trim()} onClick={submit}>
          {t('common.save')}
        </Button>
      }
    >
      <div className={styles.personCardField}>
        <Typography variant="h4" color="primary">
          {t('people.card.social.network')}
        </Typography>
        <OverflowMenu label={networkName(network, t)} ariaLabel={t('people.card.social.network')} actions={actions} selectedKey={network} />
      </div>
      <Input
        label={t('people.card.social.handle')}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={300}
        dir="ltr"
        status={errorKey ? 'error' : 'default'}
        statusMessage={errorKey ? t(errorKey) : undefined}
        autoFocus
      />
    </DialogShell>
  );
}

function WorkDialog({ personId, editing, onClose }: { personId: string; editing: CardOrganization | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [company, setCompany] = useState(editing?.company ?? '');
  const [title, setTitle] = useState(editing?.title ?? '');
  const [website, setWebsite] = useState(editing?.website ?? '');
  const { busy, errorKey, save } = useCardSave(personId, onClose);
  const empty = !company.trim() && !title.trim() && !website.trim();
  const submit = () => {
    if (empty) return;
    void save(
      {
        op: 'set',
        field: 'organization',
        value: { company: company.trim(), title: title.trim(), website: website.trim() },
      },
      editing ? 'save_work_edit' : 'save_work',
    );
  };
  return (
    <DialogShell
      title={t('people.card.work')}
      onClose={onClose}
      footer={
        <Button fullWidth loading={busy} disabled={empty} onClick={submit}>
          {t('common.save')}
        </Button>
      }
    >
      <Input label={t('people.card.work.company')} value={company} onChange={(e) => setCompany(e.target.value)} maxLength={150} autoFocus />
      <Input label={t('people.card.work.title')} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={150} />
      <Input
        label={t('people.card.work.website')}
        optionalLabel={t('common.optional')}
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        maxLength={300}
        dir="ltr"
        placeholder="https://"
      />
      <ErrorLine messageKey={errorKey} />
    </DialogShell>
  );
}

function ForgetDialog({ personId, edit, onClose }: { personId: string; edit: CardEdit; onClose: () => void }) {
  const { t } = useTranslation();
  const productName = useProductName();
  const { busy, errorKey, save } = useCardSave(personId, onClose);
  return (
    <DialogShell
      title={t('people.card.forget')}
      onClose={onClose}
      footer={
        <div className={styles.personCardFooter}>
          <Button variant="outlined" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button color="destructive" loading={busy} onClick={() => void save(edit, `confirm_forget_${edit.field}`)}>
            {t('people.card.forget')}
          </Button>
        </div>
      }
    >
      <Typography variant="p1" color="primary">
        {t('people.card.forget.confirm', { productName })}
      </Typography>
      <ErrorLine messageKey={errorKey} />
    </DialogShell>
  );
}

// ---- How you relate (P2.3) ----------------------------------------------------------------------------------------

const RELATE = 'people.card.relate';

/**
 * "How you relate" — the contract's section (native-apps/shared/people-import/card-edit-contract.md, P2.3), in its
 * order: Feeling · Closeness · Tone · Call them · Never bring up · Don't suggest reaching out, each stated row with
 * the same forget the other sections use. Symphony's own read sits behind "How I read this", collapsed by default
 * and NOT MOUNTED while collapsed, so a screenshot of the card never carries it.
 */
function RelateSection({
  personId,
  relate,
  onOpen,
  onForget,
}: {
  personId: string;
  relate: CardRelate;
  onOpen: (row: RelateRow, button: string) => void;
  onForget: (edit: CardEdit, button: string) => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [learnedOpen, setLearnedOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const forgetSlot = (slot: 'feeling' | 'closeness' | 'tone' | 'address', button: string) => () =>
    onForget({ op: 'forget', field: 'relationshipProfile', target: { slot } }, button);
  const add = (row: RelateRow, button: string, label = t('common.add')) => (
    <Button variant="text" size="small" startIcon={<Plus size={16} aria-hidden />} onClick={() => onOpen(row, button)}>
      {label}
    </Button>
  );

  const toggleDoNotApproach = async (on: boolean) => {
    if (switching) return;
    setSwitching(true);
    setSwitchError(null);
    logPageClickBi({ page_name: PAGE, button_name: 'toggle_do_not_approach', answer: on ? 'on' : 'off' });
    // On is a set; off is the row's forget (the same on all three surfaces).
    const result = await editPersonCard(
      personId,
      on
        ? { op: 'set', field: 'relationshipProfile', value: { doNotApproach: true } }
        : { op: 'forget', field: 'relationshipProfile', target: { slot: 'doNotApproach' } },
    );
    setSwitching(false);
    if (!result.ok) setSwitchError(result.messageKey);
  };

  /** One labelled row: the label, what is set (or nothing), and the row's action at its end. */
  const row = (testId: string, label: string, value: ReactNode, end: ReactNode) => (
    <Card variant="sunken" size="sm" className={styles.channelCard} data-testid={testId}>
      <div className={styles.channelRow}>
        <span className={styles.channelText}>
          <Typography variant="p3" color="secondary">
            {label}
          </Typography>
          {value}
        </span>
        {end}
      </div>
    </Card>
  );

  return (
    <div className={styles.detailSection} data-testid="person-card-relate">
      <Typography variant="h3" color="primary">
        {t(`${RELATE}.title`)}
      </Typography>
      <div className={styles.channelCards}>
        {/* 1. Feeling — one of four, with the user's own words under it. */}
        {row(
          'relate-feeling',
          t(`${RELATE}.feeling`),
          relate.feeling ? (
            <>
              <Typography variant="h4" color="primary">
                {t(`${RELATE}.feeling.${relate.feeling.value}`)}
              </Typography>
              {relate.feeling.words ? (
                <Typography variant="p2" color="secondary">
                  {relate.feeling.words}
                </Typography>
              ) : null}
            </>
          ) : null,
          relate.feeling ? (
            <RowMenu
              ariaLabel={t(`${RELATE}.feeling`)}
              onEdit={() => onOpen('feeling', 'edit_relate_feeling')}
              onForget={forgetSlot('feeling', 'forget_relate_feeling')}
            />
          ) : (
            add('feeling', 'add_relate_feeling')
          ),
        )}

        {/* 2. Closeness — the user's override; unset, the relation's own closeness applies. */}
        {row(
          'relate-closeness',
          t(`${RELATE}.closeness`),
          <Typography variant={relate.closeness ? 'h4' : 'p2'} color={relate.closeness ? 'primary' : 'secondary'}>
            {relate.closeness ? t(`${RELATE}.closeness.${relate.closeness}`) : t(`${RELATE}.closeness.fromRelationship`)}
          </Typography>,
          <RowMenu
            ariaLabel={t(`${RELATE}.closeness`)}
            onEdit={() => onOpen('closeness', 'edit_relate_closeness')}
            onForget={relate.closeness ? forgetSlot('closeness', 'forget_relate_closeness') : undefined}
          />,
        )}

        {/* 3. Tone — the register and a note. */}
        {row(
          'relate-tone',
          t(`${RELATE}.tone`),
          relate.tone ? (
            <>
              {relate.tone.register ? (
                <Typography variant="h4" color="primary">
                  {t(`${RELATE}.tone.${relate.tone.register}`)}
                </Typography>
              ) : null}
              {relate.tone.note ? (
                <Typography variant="p2" color="secondary">
                  {relate.tone.note}
                </Typography>
              ) : null}
            </>
          ) : null,
          relate.tone ? (
            <RowMenu
              ariaLabel={t(`${RELATE}.tone`)}
              onEdit={() => onOpen('tone', 'edit_relate_tone')}
              onForget={forgetSlot('tone', 'forget_relate_tone')}
            />
          ) : (
            add('tone', 'add_relate_tone')
          ),
        )}

        {/* 4. Call them — what to call the person. */}
        {row(
          'relate-address',
          t(`${RELATE}.address`),
          relate.address ? (
            <Typography variant="h4" color="primary">
              <bdi>{relate.address}</bdi>
            </Typography>
          ) : null,
          relate.address ? (
            <RowMenu
              ariaLabel={t(`${RELATE}.address`)}
              onEdit={() => onOpen('address', 'edit_relate_address')}
              onForget={forgetSlot('address', 'forget_relate_address')}
            />
          ) : (
            add('address', 'add_relate_address')
          ),
        )}

        {/* 5. Never bring up — each topic removable, "Add" while fewer than five. */}
        {row(
          'relate-avoid',
          t(`${RELATE}.avoid`),
          relate.avoid.length ? (
            <span className={styles.relateTopics}>
              {relate.avoid.map((topic) => (
                <span key={topic} className={styles.relateTopic}>
                  <Typography variant="p2" color="primary" component="span">
                    <bdi>{topic}</bdi>
                  </Typography>
                  <IconButton
                    size="tiny"
                    variant="text"
                    aria-label={`${t('people.card.forget')}: ${topic}`}
                    onClick={() => {
                      const rest = relate.avoid.filter((held) => held !== topic);
                      onForget(
                        rest.length
                          ? { op: 'set', field: 'relationshipProfile', value: { avoid: rest } }
                          : { op: 'forget', field: 'relationshipProfile', target: { slot: 'avoid' } },
                        'forget_relate_avoid',
                      );
                    }}
                  >
                    <X size={14} aria-hidden />
                  </IconButton>
                </span>
              ))}
            </span>
          ) : null,
          relate.canAddAvoid ? add('avoid', 'add_relate_avoid', t(`${RELATE}.avoid.add`)) : null,
        )}

        {/* 6. Don't suggest reaching out — a switch, saved as it is flipped. */}
        <Card variant="sunken" size="sm" className={styles.channelCard} data-testid="relate-do-not-approach">
          <Switch
            checked={relate.doNotApproach}
            disabled={switching}
            onChange={(on) => void toggleDoNotApproach(on)}
            label={t(`${RELATE}.doNotApproach`)}
            description={t(`${RELATE}.doNotApproach.help`)}
          />
          <ErrorLine messageKey={switchError} />
        </Card>
      </div>

      {/* Symphony's read — collapsed, read-only. */}
      {relate.learned ? (
        <div className={styles.relateLearned}>
          <Button
            variant="text"
            size="small"
            aria-expanded={learnedOpen}
            endIcon={<ChevronDown size={16} aria-hidden className={learnedOpen ? styles.relateChevronOpen : undefined} />}
            onClick={() => {
              logPageClickBi({ page_name: PAGE, button_name: 'toggle_relate_learned', answer: learnedOpen ? 'collapse' : 'expand' });
              setLearnedOpen((open) => !open);
            }}
          >
            {t(`${RELATE}.learned.toggle`)}
          </Button>
          {learnedOpen ? <LearnedRead learned={relate.learned} /> : null}
        </div>
      ) : null}
    </div>
  );
}

/** "How I read this", expanded: style, topics, summary, the feeling ("Seems warm") and the evidence lines. */
export function LearnedRead({ learned }: { learned: NonNullable<CardRelate['learned']> }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const block = (testId: string, label: string, body: ReactNode) => (
    <span className={styles.channelText} data-testid={testId}>
      <Typography variant="p3" color="secondary">
        {label}
      </Typography>
      {body}
    </span>
  );
  const styleLine = learned.style
    ? learnedStyleParts(learned.style)
        .map((part) => ('key' in part ? t(part.key) : 'language' in part ? languageName(part.language, locale) : part.text))
        .join(' · ')
    : '';
  return (
    <Card variant="sunken" size="sm" className={styles.channelCard} data-testid="relate-learned">
      <div className={styles.relateLearnedBody}>
        {learned.feeling ? (
          <Typography variant="h4" color="primary" data-testid="relate-learned-feeling">
            {t(`${RELATE}.learned.feeling.${learned.feeling}`)}
          </Typography>
        ) : null}
        {learned.style && (styleLine || learned.style.note)
          ? block(
              'relate-learned-style',
              t(`${RELATE}.learned.style`),
              <>
                {styleLine ? (
                  <Typography variant="p2" color="primary">
                    <bdi>{styleLine}</bdi>
                  </Typography>
                ) : null}
                {learned.style.note ? (
                  <Typography variant="p2" color="secondary">
                    {learned.style.note}
                  </Typography>
                ) : null}
              </>,
            )
          : null}
        {learned.topics.length
          ? block(
              'relate-learned-topics',
              t(`${RELATE}.learned.topics`),
              <Typography variant="p2" color="primary">
                {learned.topics.join(' · ')}
              </Typography>,
            )
          : null}
        {learned.summary
          ? block(
              'relate-learned-summary',
              t(`${RELATE}.learned.summary`),
              <Typography variant="p2" color="primary">
                {learned.summary}
              </Typography>,
            )
          : null}
        {learned.evidence.length ? (
          <ul className={styles.relateEvidence} data-testid="relate-learned-evidence">
            {learned.evidence.map((line, index) => {
              const date = formatRelateDate(line.occurredAt, locale);
              // OWNER_WORDS: only when — the model never carries what the user said.
              const textLine =
                line.kind === 'ownerWords'
                  ? t(`${RELATE}.learned.evidence.ownerWords`, { date: date ?? '' })
                  : [line.reason, date].filter(Boolean).join(' · ');
              return (
                <li key={`${line.kind}:${index}`}>
                  <Typography variant="p3" color="secondary" component="span">
                    {textLine}
                  </Typography>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </Card>
  );
}

/** The dialog behind each "How you relate" row's Add / Edit. Saves only the keys of its row. */
function RelateDialog({ personId, row, relate, onClose }: { personId: string; row: RelateRow; relate: CardRelate; onClose: () => void }) {
  const { t } = useTranslation();
  const { busy, errorKey, save } = useCardSave(personId, onClose);
  const [feeling, setFeeling] = useState<CardFeeling | null>(relate.feeling?.value ?? null);
  const [closeness, setCloseness] = useState<CardCloseness | null>(relate.closeness);
  const [register, setRegister] = useState<CardRegister | null>(relate.tone?.register ?? null);
  const [words, setWords] = useState(
    row === 'feeling' ? relate.feeling?.words ?? '' : row === 'tone' ? relate.tone?.note ?? '' : row === 'address' ? relate.address ?? '' : '',
  );
  const set = (value: Record<string, unknown>, button: string) => void save({ op: 'set', field: 'relationshipProfile', value } as CardEdit, button);

  const picker = <V extends string>(options: V[], selected: V | null, onSelect: (v: V) => void, keyOf: (v: V) => string, label: string) => (
    <div className={styles.personCardField}>
      <Typography variant="h4" color="primary">
        {label}
      </Typography>
      <OverflowMenu
        label={selected ? t(keyOf(selected)) : label}
        ariaLabel={label}
        actions={options.map((option) => ({ key: option, label: t(keyOf(option)), onSelect: () => onSelect(option) }))}
        selectedKey={selected ?? undefined}
      />
    </div>
  );

  const typed = words.trim();
  const config: Record<RelateRow, { title: string; ready: boolean; submit: () => void; body: ReactNode }> = {
    feeling: {
      title: t(`${RELATE}.feeling`),
      ready: !!feeling,
      submit: () => feeling && set({ feeling: FEELING_WIRE[feeling], feelingWords: typed }, 'save_relate_feeling'),
      body: (
        <>
          {picker(FEELINGS, feeling, setFeeling, (v) => `${RELATE}.feeling.${v}`, t(`${RELATE}.feeling`))}
          <Input
            label={t(`${RELATE}.feelingWords.placeholder`)}
            optionalLabel={t('common.optional')}
            value={words}
            onChange={(e) => setWords(e.target.value)}
            placeholder={t(`${RELATE}.feelingWords.placeholder`)}
            maxLength={RELATIONSHIP_PROFILE_CAPS.feelingWords}
          />
        </>
      ),
    },
    closeness: {
      title: t(`${RELATE}.closeness`),
      ready: !!closeness,
      submit: () => closeness && set({ closeness: CLOSENESS_WIRE[closeness] }, 'save_relate_closeness'),
      body: picker(CLOSENESSES, closeness, setCloseness, (v) => `${RELATE}.closeness.${v}`, t(`${RELATE}.closeness`)),
    },
    tone: {
      title: t(`${RELATE}.tone`),
      ready: !!register || !!typed,
      submit: () => set({ ...(register ? { register: REGISTER_WIRE[register] } : {}), toneNote: typed }, 'save_relate_tone'),
      body: (
        <>
          {picker(REGISTERS, register, setRegister, (v) => `${RELATE}.tone.${v}`, t(`${RELATE}.tone`))}
          <Input
            label={t(`${RELATE}.toneNote.placeholder`)}
            optionalLabel={t('common.optional')}
            value={words}
            onChange={(e) => setWords(e.target.value)}
            placeholder={t(`${RELATE}.toneNote.placeholder`)}
            maxLength={RELATIONSHIP_PROFILE_CAPS.toneNote}
          />
        </>
      ),
    },
    address: {
      title: t(`${RELATE}.address`),
      ready: !!typed,
      submit: () => typed && set({ address: typed }, 'save_relate_address'),
      body: (
        <Input
          label={t(`${RELATE}.address`)}
          value={words}
          onChange={(e) => setWords(e.target.value)}
          maxLength={RELATIONSHIP_PROFILE_CAPS.address}
          autoFocus
        />
      ),
    },
    avoid: {
      title: t(`${RELATE}.avoid`),
      ready: !!typed,
      // The avoid list is written WHOLE: the stored topics plus this one.
      submit: () => typed && set({ avoid: [...relate.avoid, typed] }, 'save_relate_avoid'),
      body: (
        <Input
          label={t(`${RELATE}.avoid`)}
          value={words}
          onChange={(e) => setWords(e.target.value)}
          maxLength={RELATIONSHIP_PROFILE_CAPS.avoidItem}
          autoFocus
        />
      ),
    },
  };
  const current = config[row];
  return (
    <DialogShell
      title={current.title}
      onClose={onClose}
      footer={
        <Button fullWidth loading={busy} disabled={!current.ready} onClick={current.submit}>
          {t('common.save')}
        </Button>
      }
    >
      {current.body}
      <ErrorLine messageKey={errorKey} />
    </DialogShell>
  );
}
